import { Component } from 'react';
import { Link } from 'react-router-dom';
import ErrorState from './ui/ErrorState';
import { isChunkLoadError, tryReloadForStaleChunk } from '../lib/chunkLoadError';
import {
  PROTECTED_ROUTE_ERROR_DASHBOARD_LABEL,
  PROTECTED_ROUTE_ERROR_RELOAD_LABEL,
  PROTECTED_ROUTE_ERROR_RETRY_LABEL,
} from '../lib/protectedRouteErrorCopy';

/**
 * Catches render errors for a single route so the rest of the app shell stays mounted.
 */
export class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[RouteErrorBoundary]', error, info?.componentStack);
    if (isChunkLoadError(error) && tryReloadForStaleChunk()) {
      return;
    }
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isStaleChunk = isChunkLoadError(this.state.error);
    const title = this.props.title ?? 'Something went wrong';
    const description = isStaleChunk
      ? 'A new version of FuelCyclePro was deployed while you had this tab open. Reload the page to load the latest app.'
      : (this.props.description ??
        'This page hit an unexpected error. Try again or go back to your dashboard.');

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg space-y-4">
          <ErrorState
            title={title}
            description={description}
            retryLabel={PROTECTED_ROUTE_ERROR_RETRY_LABEL}
            onRetry={() => {
              this.handleReset();
              this.props.onRetry?.();
            }}
          />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-secondary btn-compact"
            >
              {PROTECTED_ROUTE_ERROR_RELOAD_LABEL}
            </button>
            <Link
              to="/dashboard"
              className="btn-primary btn-compact inline-flex items-center justify-center"
            >
              {PROTECTED_ROUTE_ERROR_DASHBOARD_LABEL}
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
